/**
 * Drops Repository - Data access layer for drops
 */

import api from "../../../shared/api.jsx";

const DROPS_ENDPOINTS = {
  base: "/drops",
  byId: (id) => `/drops/${id}`,
  byTrip: (tripId) => `/drops/trips/${tripId}/drops`,
  status: (id) => `/drops/${id}/status`,
  complete: (id) => `/drops/${id}/complete`,
  orders: (id) => `/drops/${id}/orders`,
  order: (id, orderId) => `/drops/${id}/orders/${orderId}`,
  orderDelivery: (id, orderId) => `/drops/${id}/orders/${orderId}/delivery`,
};

/**
 * Get all drops for a trip
 * @param {string} tripId - Trip identifier
 * @returns {Promise<Object>} API response
 */
export const fetchDropsByTrip = async (tripId) => {
  const response = await api.get(DROPS_ENDPOINTS.byTrip(tripId));
  // API client returns JSON directly, backend returns { success: true, data: [...] }
  return {
    success: response.success !== false,
    data: response.data || response,
  };
};

/**
 * Get a single drop by ID
 * @param {string} dropId - Drop identifier
 * @returns {Promise<Object>} API response
 */
export const fetchDropById = async (dropId) => {
  const response = await api.get(DROPS_ENDPOINTS.byId(dropId));
  // API client returns JSON directly, backend returns { success: true, data: {...} }
  return {
    success: response.success !== false,
    data: response.data || response,
  };
};

/**
 * Create a new drop for a trip
 * @param {string} tripId - Trip identifier
 * @param {Object} dropData - Drop data
 * @returns {Promise<Object>} API response
 */
export const createDrop = async (tripId, dropData) => {
  const response = await api.post(DROPS_ENDPOINTS.byTrip(tripId), dropData);
  return response;
};

/**
 * Update an existing drop
 * @param {string} dropId - Drop identifier
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>} API response
 */
export const updateDrop = async (dropId, updateData) => {
  const response = await api.put(DROPS_ENDPOINTS.byId(dropId), updateData);
  return response;
};

/**
 * Update drop status
 * @param {string} dropId - Drop identifier
 * @param {string} status - New status
 * @returns {Promise<Object>} API response
 */
export const updateDropStatus = async (dropId, status) => {
  const response = await api.patch(DROPS_ENDPOINTS.status(dropId), {
    status,
  });
  return response;
};

/**
 * Complete drop with driver confirmation
 * @param {string} dropId - Drop identifier
 * @param {Object} confirmationData - Confirmation data
 * @returns {Promise<Object>} API response
 */
export const completeDrop = async (dropId, confirmationData) => {
  const response = await api.post(DROPS_ENDPOINTS.complete(dropId), confirmationData);
  return response;
};

/**
 * Add order to drop
 * @param {string} dropId - Drop identifier
 * @param {string} orderId - Order identifier
 * @param {Object} [orderData] - Order data
 * @returns {Promise<Object>} API response
 */
export const addOrderToDrop = async (dropId, orderId, orderData = {}) => {
  const response = await api.post(DROPS_ENDPOINTS.orders(dropId), {
    orderId,
    ...orderData,
  });
  return response;
};

/**
 * Update delivery progress for an order in a drop
 * @param {string} dropId - Drop identifier
 * @param {string} orderId - Order identifier
 * @param {number} itemsDelivered - Number of items delivered
 * @returns {Promise<Object>} API response
 */
export const updateOrderDeliveryProgress = async (dropId, orderId, itemsDelivered, options = {}) => {
  const response = await api.patch(DROPS_ENDPOINTS.orderDelivery(dropId, orderId), {
    itemsDelivered,
    skipEmail: options.skipEmail,
    includeReviewRequest: options.includeReviewRequest,
  });
  // API client returns JSON directly, backend returns { success: true, data: {...} }
  return {
    success: response.success !== false,
    data: response.data || response,
  };
};

/**
 * Remove order from drop
 * @param {string} dropId - Drop identifier
 * @param {string} orderId - Order identifier
 * @returns {Promise<Object>} API response
 */
export const removeOrderFromDrop = async (dropId, orderId) => {
  const response = await api.delete(DROPS_ENDPOINTS.order(dropId, orderId));
  return response;
};

/**
 * Delete a drop
 * @param {string} dropId - Drop identifier
 * @returns {Promise<Object>} API response
 */
export const deleteDrop = async (dropId) => {
  const response = await api.delete(DROPS_ENDPOINTS.byId(dropId));
  return response;
};

